/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { $NodeFilters, GetNodesData, GetNodesResponse, Node, NodeFilters, NodeSortableFields } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedNodesQueryParams = {
  params?: Omit<GetNodesData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
  urlPrefix?: string;
};

/**
 * Hook for fetching paginated nodes with filtering, sorting and real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated nodes response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export const usePaginatedNodesQuery = ({ params, type, urlPrefix }: UsePaginatedNodesQueryParams) => {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const filters = params?.filters || {};
  // Apply default subtenant filter if not explicitly provided
  if (!filters.subtenantId) {
    filters.subtenantId = selectedSubtenantId || 'null';
  }

  return usePaginatedQuery<GetNodesResponse, Node, NodeFilters, NodeSortableFields, GetNodesData>({
    apiCall: mgmtPlaneAPIClient.nodes.getNodes.bind(mgmtPlaneAPIClient.nodes),
    filterTypes: jsonSchemaToTypeMap($NodeFilters),
    urlPrefix,
    queryKey: 'nodes',
    realtimeEvents: [
      'node-config-revision:created',
      'node:operational-state-changed',
      'node:tag-added',
      'node:tag-deleted',
      'node:connectivity-updated',
      'node:updated',
      'node-config:assigned',
      'node-config:applied',
      'node:credential-rotated',
      'node:new-lifecycle-event',
      'hallmarks:cleared',
      'subtenant-membership:updated',
      'node:archived',
    ],
    params: {
      tenantId: selectedTenantId!,
      filters,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
};
